<template>
  <div class="page-wrapper">
    <div class="page-toolbar">
      <div class="page-toolbar-left">
        <div class="search-box">
          <el-icon><Search /></el-icon>
          <input v-model="searchQuery" type="text" placeholder="搜索姓名、专长" />
        </div>
      </div>
    </div>

    <div v-if="loading" class="loading-state">
      <el-icon class="loading-icon"><Loading /></el-icon>
      <p>加载中...</p>
    </div>

    <div v-else-if="filteredCoaches.length === 0" class="empty-state">
      <el-icon><User /></el-icon>
      <p>暂无教练</p>
    </div>

    <div v-else class="coach-grid">
      <div class="coach-card" v-for="(coach, i) in filteredCoaches" :key="coach.coachId">
        <div class="coach-card-top">
          <div class="coach-avatar" :style="{ background: colors[i % colors.length] }">
            {{ (coach.name || '?').charAt(0) }}
          </div>
          <span class="coach-status-badge active">在职</span>
        </div>
        <div class="coach-card-body">
          <h3 class="coach-name">{{ coach.name }}</h3>
          <p class="coach-title">{{ coach.title || '健身教练' }}</p>
          <div class="coach-tags">
            <span class="coach-tag" v-for="tag in (coach.specialty ? coach.specialty.split(',') : [])"
              :key="tag">{{ tag }}</span>
          </div>
          <div class="coach-info-row">
            <span><el-icon><Trophy /></el-icon>{{ coach.certificate || '暂无' }}</span>
            <span><el-icon><TrendCharts /></el-icon>{{ coach.experience || 0 }}年经验</span>
          </div>
          <div class="coach-footer">
            <span class="coach-phone"><el-icon><Phone /></el-icon>{{ coach.phone || '-' }}</span>
            <span class="coach-rating">
              <el-icon><Star /></el-icon>
              {{ coach.rating != null ? (coach.rating / 10).toFixed(1) : '-' }}
            </span>
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { Search, User, Phone, Star, Trophy, TrendCharts, Loading } from '@element-plus/icons-vue';
import { ElMessage } from 'element-plus';
import api from '@/api/request';

const colors = ['#0062FF', '#10B981', '#EF4444', '#EC4899', '#8B5CF6', '#F59E0B', '#06B6D4'];

export default {
  name: 'MemberCoachesView',
  components: { Search, User, Phone, Star, Trophy, TrendCharts, Loading },
  data() {
    return {
      colors,
      loading: false,
      searchQuery: '',
      coaches: [],
    };
  },
  computed: {
    filteredCoaches() {
      const list = this.coaches.filter(c => Number(c.status) === 1);
      const q = (this.searchQuery || '').trim();
      if (!q) return list;
      return list.filter(c =>
        (c.name && c.name.includes(q)) ||
        (c.specialty || '').includes(q)
      );
    },
  },
  mounted() {
    this.loadCoaches();
  },
  methods: {
    async loadCoaches() {
      this.loading = true;
      try {
        const res = await api.getCoachList();
        if (res.data && res.data.code === 200) {
          this.coaches = res.data.data || [];
        }
      } catch (e) {
        ElMessage.error('加载教练列表失败');
      } finally {
        this.loading = false;
      }
    },
  },
};
</script>

<style scoped>
.page-wrapper { padding: 0; background: var(--bg-base); min-height: 100vh; }
.page-toolbar { display: flex; align-items: center; margin-bottom: 20px; gap: 12px; flex-wrap: wrap; }
.page-toolbar-left { display: flex; align-items: center; gap: 12px; flex-wrap: wrap; flex: 1; }
.search-box { display: flex; align-items: center; background: var(--bg-white); border: 1px solid var(--border-color); border-radius: var(--radius-md); padding: 0 12px; height: 38px; min-width: 200px; max-width: 300px; gap: 8px; transition: all 0.3s; }
.search-box:focus-within { border-color: var(--primary-blue); box-shadow: 0 0 0 3px rgba(0,98,255,0.08); }
.search-box .el-icon { color: var(--text-muted); }
.search-box input { border: none; background: transparent; color: var(--text-primary); font-size: 14px; outline: none; flex: 1; }
.search-box input::placeholder { color: var(--text-muted); }
.loading-state, .empty-state { display: flex; flex-direction: column; align-items: center; justify-content: center; padding: 80px 0; color: var(--text-muted); gap: 12px; font-size: 15px; }
.loading-state .el-icon, .empty-state .el-icon { font-size: 48px; }
.loading-icon { animation: spin 1s linear infinite; }
@keyframes spin { from { transform: rotate(0deg); } to { transform: rotate(360deg); } }
.coach-grid { display: grid; grid-template-columns: repeat(auto-fill, minmax(260px, 1fr)); gap: 20px; }
.coach-card { background: var(--bg-white); border: 1px solid var(--border-color); border-radius: var(--radius-xl); overflow: hidden; transition: all 0.3s; }
.coach-card:hover { transform: translateY(-4px); box-shadow: var(--shadow-xl); }
.coach-card-top { background: var(--gradient-primary); padding: 24px 20px 20px; display: flex; align-items: flex-start; justify-content: space-between; }
.coach-avatar { width: 60px; height: 60px; border-radius: 50%; display: flex; align-items: center; justify-content: center; color: white; font-size: 24px; font-weight: 700; border: 3px solid rgba(255,255,255,0.4); background: rgba(255,255,255,0.2); }
.coach-status-badge { padding: 4px 10px; border-radius: 20px; font-size: 11px; font-weight: 600; }
.coach-status-badge.active { background: rgba(16,185,129,0.2); color: #10B981; border: 1px solid rgba(16,185,129,0.3); }
.coach-card-body { padding: 16px; }
.coach-name { font-size: 17px; font-weight: 700; color: var(--text-primary); margin: 0 0 4px; }
.coach-title { font-size: 13px; color: var(--text-secondary); margin: 0 0 12px; }
.coach-tags { display: flex; flex-wrap: wrap; gap: 6px; margin-bottom: 12px; }
.coach-tag { padding: 3px 10px; background: rgba(0,98,255,0.08); color: var(--primary-blue); border-radius: 20px; font-size: 11px; font-weight: 600; }
.coach-info-row { display: flex; gap: 16px; margin-bottom: 12px; flex-wrap: wrap; }
.coach-info-row span { font-size: 12px; color: var(--text-secondary); display: flex; align-items: center; gap: 4px; overflow-wrap: anywhere; }
.coach-footer { display: flex; align-items: center; justify-content: space-between; }
.coach-phone { font-size: 12px; color: var(--text-muted); display: flex; align-items: center; gap: 4px; }
.coach-rating { font-size: 14px; font-weight: 700; color: #F59E0B; display: flex; align-items: center; gap: 3px; }

@media (max-width: 768px) {
  .search-box { min-width: 0; max-width: none; width: 100%; flex: 1; }
}

@media (max-width: 480px) {
  .coach-grid { grid-template-columns: 1fr; gap: 14px; }
}
</style>
